import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { services } from '@/mocks/services';

const whatsappNumbers = [
  { number: '2250715021015', label: '+225 07 15 02 10 15', display: 'Numéro principal' },
  { number: '2250757006969', label: '+225 07 57 00 69 69', display: 'Numéro secondaire' },
  { number: '2250506005550', label: '+225 05 06 00 55 50', display: 'Numéro tertiaire' },
];

export default function RdvPage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    date: '',
    time: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const buildMessage = () => {
    const serviceName = services.find((s) => s.id.toString() === formData.service)?.name || formData.service;
    return encodeURIComponent(
      `Bonjour KDS BEAUTY, je souhaite réserver un rendez-vous.\nNom : ${formData.name}\nTéléphone : ${formData.phone}\nService : ${serviceName}\nDate souhaitée : ${formData.date}\nHeure souhaitée : ${formData.time}`
    );
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    // Open all 3 WhatsApp conversations
    const message = buildMessage();
    whatsappNumbers.forEach((wn, i) => {
      setTimeout(() => {
        window.open(`https://wa.me/${wn.number}?text=${message}`, '_blank');
      }, i * 600);
    });
  };

  const handleSingleWhatsApp = (number: string) => {
    const message = buildMessage();
    window.open(`https://wa.me/${number}?text=${message}`, '_blank');
  };

  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  return (
    <Layout>
      {/* Hero */}
      <section className="relative w-full h-[38vh] min-h-[280px] overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://storage.readdy-site.link/project_files/0232c479-757c-417f-9996-0a4bc3a6e43d/8b2d8f8e-9f53-45b8-bca2-d80c28f7878e_compressed_000222222.webp"
            alt="Réservation KDS BEAUTY"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-foreground-950/55" />
        </div>
        <div className="relative z-10 text-center px-4 w-full">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/20 mb-4">
            <span className="text-background-50/90 text-xs tracking-[0.15em] uppercase font-label">Réservation rapide</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-background-50 font-light mb-3">Prendre Rendez-vous</h1>
          <p className="text-background-50/70 text-sm md:text-base max-w-xl mx-auto">
            Formulaire simplifié — confirmation rapide via WhatsApp
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="w-full py-12 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          {!submitted ? (
            <div className="max-w-lg mx-auto">
              <div className="bg-background-50 rounded-3xl p-6 md:p-10 border border-background-200/60">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-800 mb-1">Votre réservation</h2>
                <p className="text-sm text-foreground-500 mb-7">Remplissez ce formulaire et envoyez directement sur WhatsApp</p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1">Nom complet</label>
                    <input
                      type="text" required
                      value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Prénom et Nom"
                      className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200/70 rounded-xl text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1">Numéro de téléphone</label>
                    <div className="flex items-center">
                      <span className="px-3 py-3 text-sm bg-background-100 text-foreground-600 border border-background-200/70 border-r-0 rounded-l-xl font-medium">+225</span>
                      <input
                        type="tel" required
                        value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="07 00 00 00 00"
                        className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200/70 rounded-r-xl text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                  </div>

                  {/* Service */}
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1">Service souhaité</label>
                    <select
                      required
                      value={formData.service} onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200/70 rounded-xl text-foreground-700 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer"
                    >
                      <option value="">Choisissez votre soin</option>
                      {services.map((s) => (
                        <option key={s.id} value={s.id.toString()}>
                          {s.name} — {s.price.toLocaleString('fr-FR')} FCFA
                        </option>
                      ))}
                      <option value="Forfait bien-être">Forfait bien-être (sur mesure)</option>
                      <option value="Autre demande">Autre demande</option>
                    </select>
                  </div>

                  {/* Date + Time row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1">Date souhaitée</label>
                      <input
                        type="date" required min={today} max={maxDate}
                        value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200/70 rounded-xl text-foreground-700 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1">Heure souhaitée</label>
                      <input
                        type="time" required
                        value={formData.time} onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                        min="09:00" max="19:00"
                        className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200/70 rounded-xl text-foreground-700 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* Submit button */}
                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-medium bg-green-600 text-white hover:bg-green-700 transition-all duration-300 shadow-lg shadow-green-600/20"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-whatsapp-line text-lg" />
                    </div>
                    Envoyer ma demande via WhatsApp
                  </button>
                </form>
              </div>

              {/* Quick contact cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
                {whatsappNumbers.map((wn) => (
                  <a
                    key={wn.number}
                    href={`https://wa.me/${wn.number}?text=Bonjour%20KDS%20BEAUTY%2C%20je%20souhaite%20prendre%20un%20rendez-vous.`}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-green-50 border border-green-200/60 text-green-700 hover:bg-green-100 transition-all duration-300 text-sm font-medium"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-whatsapp-line" />
                    </div>
                    {wn.label}
                  </a>
                ))}
              </div>

              {/* Contact info minimal */}
              <div className="mt-6 p-5 rounded-2xl bg-background-50 border border-background-200/60">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-foreground-500 mb-0.5">Adresse</p>
                    <p className="text-sm font-medium text-foreground-800">Angré Château, Carrefour Cabri</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-0.5">Horaires</p>
                    <p className="text-sm font-medium text-foreground-800">Lun-Ven 09h-19h · Sam 09h-20h</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-0.5">Téléphone</p>
                    <p className="text-sm font-medium text-foreground-800">+225 07 15 02 10 15</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Success state */
            <div className="max-w-lg mx-auto text-center py-16">
              <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-green-100">
                <i className="ri-check-line text-3xl text-green-600" />
              </div>
              <h3 className="font-heading text-2xl text-foreground-800 mb-2">Demande envoyée !</h3>
              <p className="text-sm text-foreground-600 mb-3">
                Les conversations WhatsApp se sont ouvertes. Notre équipe vous confirmera votre rendez-vous dans les plus brefs délais.
              </p>
              <p className="text-xs text-foreground-500 mb-8">
                Si les conversations ne se sont pas ouvertes, cliquez sur l'un des numéros ci-dessous :
              </p>

              {/* Fallback buttons */}
              <div className="space-y-3 mb-8">
                {whatsappNumbers.map((wn) => (
                  <button
                    key={wn.number}
                    onClick={() => handleSingleWhatsApp(wn.number)}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium bg-green-600 text-white hover:bg-green-700 transition-all duration-300"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-whatsapp-line" />
                    </div>
                    Envoyer à {wn.label}
                  </button>
                ))}
              </div>

              <Link
                to="/"
                className="inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-medium border border-background-300/80 text-foreground-700 hover:bg-background-100 transition-all"
              >
                Retour à l'accueil
              </Link>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}